import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { Text, TextInput, View } from "react-native";
import CountryFlag from "react-native-country-flag";

import { sendRequest } from "@/utils/ApiUtil";

interface InputPhoneProps {
  control: any;
  name: string;
  label: string;
  handleChange?: (text: string) => void;
  type?: string;
  optional?: boolean;
  withOutDefault?: boolean;
}

interface CountryData {
  country: string;
  translation: string;
  code: string;
  svg: string;
  idd: string;
}

export default function InputPhone(props: InputPhoneProps) {
  const defaultCountry = {
    country: "United States",
    translation: "Estados Unidos",
    code: "US",
    svg: "usa.svg",
    idd: "+1",
  };

  const [textInputFocused, setTextInputFocused] = useState(false);
  const [ghostInput, setGhostInput] = useState("");
  const [flags, setFlags] = useState<CountryData[]>([]);
  const [phone, setPhone] = useState(
    props.withOutDefault ? null : defaultCountry.idd,
  );
  const [selectedCountry, setSelectedCountry] = useState<CountryData | null>(
    props.withOutDefault ? null : defaultCountry,
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest("get", "/countries");
        if (response && Array.isArray(response)) {
          setFlags(response);
        }
      } catch (error) {
        console.log(error);
      }
    };

    fetchData();
  }, []);

  const handleChangeText = (text: string, onChange: (text: string) => void) => {
    if (/^[+0-9]+$/.test(text)) {
      if (text.charAt(0) !== "+") {
        setPhone("+" + text);
        onChange("+" + text);
        props.handleChange?.("+" + text);
      } else {
        setPhone(text);
        onChange(text);
        props.handleChange?.(text);
      }
      const matchedCountry = flags.find((country) => country.idd === text);
      if (matchedCountry) {
        setSelectedCountry(matchedCountry);
        setGhostInput("");
      }
    } else {
      const textInput = text.replace(/[\d+]/g, "");
      const newCountryName = ghostInput + textInput;
      setGhostInput(newCountryName);

      const matchedCountryByName = flags.find(
        (country) =>
          country.country.toLowerCase() === newCountryName.toLowerCase(),
      );

      if (matchedCountryByName) {
        setSelectedCountry(matchedCountryByName);
        setPhone(matchedCountryByName.idd);
        setGhostInput("");
      }
    }
  };

  return (
    <View>
      {props.optional === true ? (
        <View className="flex-row m-2">
          <Text className="text-black text-sm">{props.label}</Text>
          <Text className="text-midgray text-sm italic">{` (${t(
            "common:placeholder.optional",
          )})`}</Text>
        </View>
      ) : (
        <Text className="text-black text-sm m-2">{props.label}</Text>
      )}
      <Controller
        control={props.control}
        name={props.name}
        defaultValue=""
        render={({
          field: { onChange, value, ref },
          fieldState: { error },
        }) => (
          <>
            <View className="flex-row">
              <View
                className={`absolute items-center justify-center p-4 h-full ${
                  textInputFocused
                    ? "border-r border-black1"
                    : "border-r border-midgray"
                }`}
              >
                <View style={{ overflow: "hidden" }}>
                  {selectedCountry && (
                    <CountryFlag
                      isoCode={selectedCountry.code.toLowerCase()}
                      size={17}
                    />
                  )}
                </View>
              </View>
              <TextInput
                ref={ref}
                onFocus={() => setTextInputFocused(true)}
                onBlur={() => setTextInputFocused(false)}
                className={`border border-solid rounded-xl w-80 h-14 px-16 text-black1 ${
                  error ? "border-red" : "border-midgray"
                }`}
                value={!value ? phone : value}
                defaultValue=""
                onChangeText={(text) => handleChangeText(text, onChange)}
              />
            </View>
            <View style={{ height: 20, overflow: "hidden" }}>
              {error && (
                <Text className="text-red text-xs pl-2">{error.message}</Text>
              )}
            </View>
          </>
        )}
      />
    </View>
  );
}
